/**
 * constants/transactionType.ts
 * Transaction type display metadata for balance history UI.
 *
 * Provides human-friendly display names and an ordered type list for
 * the 8 transaction types used in BalanceHistory records.
 *
 * Corresponds to the `transactionType` field in `types/schema/BalanceHistory.ts`.
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Zero cross-domain imports (only `import type` from `types/`)
 * - No side effects, pure data only
 * - All exports use `as const satisfies` for compile-time type safety
 */

import type { BalanceHistory } from '@/types/schema/BalanceHistory';

/**
 * Ordered array of all transaction type values.
 * Used for iteration in UI dropdowns and selectors.
 */
export const TRANSACTION_TYPES = [
  'adjustment',
  'deposit',
  'dividend',
  'fee',
  'interest',
  'other',
  'trade',
  'withdrawal',
] as const satisfies readonly BalanceHistory['transactionType'][];

/**
 * Human-friendly display names for each transaction type.
 * Keyed by `transactionType` for direct lookup in UI components.
 */
export const TRANSACTION_TYPE_DISPLAY_MAP = {
  'adjustment': 'Adjustment',
  'deposit': 'Deposit',
  'dividend': 'Dividend',
  'fee': 'Fee',
  'interest': 'Interest',
  'other': 'Other',
  'trade': 'Trade P&L',
  'withdrawal': 'Withdrawal',
} as const satisfies Readonly<Record<BalanceHistory['transactionType'], string>>;

/**
 * Compile-time exhaustiveness check ensuring all transactionType values are covered.
 *
 * Reduces to `true` when every transactionType union member exists as a key in
 * TRANSACTION_TYPE_DISPLAY_MAP. Adding a new transactionType without updating the map
 * will cause a compilation error.
 */
type _AllTransactionTypesCovered =
  Exclude<BalanceHistory['transactionType'], keyof typeof TRANSACTION_TYPE_DISPLAY_MAP> extends never ? true : never;

/**
 * @internal
 * Compile-time coverage check — bundled export to eliminate unused variable warnings
 * while maintaining drift protection. Do NOT re-export from barrels.
 */
export type _TransactionTypeChecks = _AllTransactionTypesCovered;
